import { SortKey, SortOrder } from "ui/Table/ColumnHeader";

export type SortProps = {
  sortBy: SortKey;
  sortOrder: "1" | "-1";
  numericProperties?: string[];
  dateProperties?: string[];
};

type HandleTableDataSortingProps = {
  sortBy: SortKey;
  sortOrder: SortOrder;
  numericProperties?: string[];
  dateProperties?: string[];
  numericWithSeparator?: {
    propertyName: string;
    separator: string;
  };
};

/**
 * @T -> type of the object to compare.
 * @param sortBy -> sorting key property.
 * @param sortOrder -> sorting order (1, -1).
 * @param numericProperties -> array of numeric properties, for applying corresponding operator based on type  (Comparision for 'string' and Arithmetic for 'number').
 * @param dateProperties -> array of date properties.
 *
 */
export const handleTableDataSorting = <T>({
  sortBy,
  sortOrder,
  numericProperties,
  numericWithSeparator,
  dateProperties,
}: HandleTableDataSortingProps) => {
  return (a: T | any, b: T | any): number => {
    if (typeof sortBy === "string") {
      // sort for numeric numbers with string separator.
      if (
        numericWithSeparator &&
        numericWithSeparator.propertyName === sortBy
      ) {
        const splitedValueA: string[] = a[
          numericWithSeparator.propertyName
        ].split(numericWithSeparator.separator);
        const splitedValueB: string[] = b[
          numericWithSeparator.propertyName
        ].split(numericWithSeparator.separator);

        // compare each value of the 2 arrays.
        for (let index = 0; index < splitedValueA.length; index++) {
          // if values are equal then, continue to the next value.
          if (
            parseInt(splitedValueA[index]) - parseInt(splitedValueB[index]) ===
            0
          ) {
            continue;
          } else {
            // if values are not equal then, return the substraction.
            return sortOrder === "1"
              ? parseInt(splitedValueA[index]) - parseInt(splitedValueB[index])
              : parseInt(splitedValueB[index]) - parseInt(splitedValueA[index]);
          }
        }
      }
      // sort for date values
      if (dateProperties && dateProperties.includes(sortBy)) {
        const dateA = new Date(a[sortBy]).getTime();
        const dateB = new Date(b[sortBy]).getTime();
        return sortOrder === "1" ? dateA - dateB : dateB - dateA;
      }
      // sort for numeric values
      if (numericProperties && numericProperties.includes(sortBy)) {
        return sortOrder === "1"
          ? a[sortBy] - b[sortBy]
          : b[sortBy] - a[sortBy];
      }
      // sort for string values
      if (a[sortBy] < b[sortBy]) {
        return sortOrder === "1" ? -1 : 1;
      }
      if (a[sortBy] > b[sortBy]) {
        return sortOrder === "1" ? 1 : -1;
      } else {
        return 0;
      }
    } else {
      // sort by nested value
      if (a[sortBy.first][sortBy.second] < b[sortBy.first][sortBy.second]) {
        return sortOrder === "1" ? -1 : 1;
      }
      if (a[sortBy.first][sortBy.second] > b[sortBy.first][sortBy.second]) {
        return sortOrder === "1" ? 1 : -1;
      } else {
        return 0;
      }
    }
  };
};
